import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import useOfferList from 'hooks/Offers/useOfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import {
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import { EmptyObject } from 'lib/object/objectUtils'
import OfferListCarousel from '../OfferListCarousel'
import HeroOfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/HeroOfferCarouselLookingForSomethingElse'
import PaletteSheet from 'LuxKit/PaletteSheet'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import {
  isTourCampaignActive,
  TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
  TOUR_CAMPAIGN_CONFIG,
  TOUR_CAMPAIGN_SEARCH_FILTER_TAG,
} from 'constants/tours'
import SiteTakeoverBackgroundImage from 'takeovers/utils/components/SiteTakeoverBackgroundImage'
import FilterChip from 'LuxKit/Chips/FilterChip'
import Places from 'constants/places'
import { useLocalizedPlaces } from 'hooks/usePlace'
import CarouselViewAllLink from 'components/Common/CarouselViewAllLink'
import { take } from 'lib/array/arrayUtils'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import TourAsiaCampaignProfileImage from 'takeovers/tourBestAsiaPromoTakeover/components/TourAsiaCampaignProfileImage'
import TourAsiaCampaignHeroCarouselSubtitle from 'takeovers/tourBestAsiaPromoTakeover/components/TourAsiaCampaignHeroCarouselSubtitle'
import TourAsiaCampaignTakeoverBackground from 'takeovers/tourBestAsiaPromoTakeover/components/TourAsiaCampaignTakeoverBackground'
import AgentHubEarnYourOwnTourBanner from 'agentHub/components/AgentHubEarnYourOwnTourBanner'
import Sheet from 'components/utils/Sheet'
import clsx from 'clsx'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import {
  getExclusiveOfferCarouselSurface,
  hasCustomHeroOfferCarouselSurface,
} from 'home/components/OfferCarousels/heroOfferCarouselSurface'

interface Props {
  filters?: App.OfferListFilters
  random?: boolean
  onOffersShow?: (offerIds: Array<string>) => void
}

const SiteTakeoverBackground = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
`

const Content = styled(Group)`
  position: relative;
  z-index: 1;
`

const ExclusiveOfferContainer = styled(PaletteSheet)`
  position: relative;
  padding: ${rem(64)} 0 ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    padding: ${rem(88)} 0 ${rem(40)};
  }

  /*
   * Teal = Highlights / Primary / DarkBackground.
   * Dual gate must share useRebrandVersion() with ThemeProvider / body.app-palette-rebrand
   * (see HotelExclusiveOfferCarousel) so OPTIMIZELY_* env override stays in sync.
   * Prefer &&.class over nested && under body… (nested && emits a broken selector).
   */
  body.${REBRAND_PALETTE_CLASS} &&.rebrand-highlight {
    background-color: var(--palette-highlight-primary-dark-background);
  }
`

const TopExclusiveHeaderGrid = styled(PaletteSheet)`
  display: grid;
  align-items: center;
  grid-template:
    "title link"
    / minmax(0, 1fr) max-content;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    display: flex;
    gap: ${rem(16)};
  }

  > .title-area {
    grid-area: title;

    ${mediaQueryUp.tablet} {
      flex: 1;
      min-width: 0;
    }
  }

  > .link-area {
    grid-area: link;
  }

  > .filters-area {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  &.campaign {
    grid-template: "title" / 1fr;
  }
`

const CampaignHeaderContent = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(4)};
`

const CampaignTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(16)};
  }
`

export function getLuxExclusiveToursSearchUrl(): string {
  const urlSearchParams = new URLSearchParams({
    destinationId: ANYWHERE_PLACE_ID,
    destinationName: ANYWHERE_NAME,
    categories: 'lux_exclusive',
  })

  return `/search/tours?${urlSearchParams.toString()}`
}

const CampaignActions = styled(Group)`
  flex-shrink: 0;
`

interface CountdownTimeParts {
  days: number
  hours: number
  isUrgent: boolean
  minutes: number
}

const URGENT_COUNTDOWN_THRESHOLD_MS = 72 * 60 * 60 * 1000

function getCountdownTimeParts(endDate: string): CountdownTimeParts | null {
  const endTime = new Date(endDate).getTime()
  const remainingMs = endTime - Date.now()

  if (Number.isNaN(endTime) || remainingMs <= 0) {
    return null
  }

  const totalMinutes = Math.floor(remainingMs / 1000 / 60)
  const days = Math.floor(totalMinutes / 60 / 24)
  const hours = Math.floor((totalMinutes / 60) % 24)
  const minutes = totalMinutes % 60

  return { days, hours, isUrgent: remainingMs <= URGENT_COUNTDOWN_THRESHOLD_MS, minutes }
}

function formatCountdownValue(value: number): string {
  return value.toString().padStart(2, '0')
}

const CountdownWrapper = styled.div<{ $isUrgent: boolean }>`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  border-radius: ${rem(4)};
  background-color: ${props => props.$isUrgent ?
    props.theme.palette.messaging.critical.normalBackground :
    'transparent'};
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  padding: ${props => props.$isUrgent ? `${rem(6)} ${rem(8)}` : 0};
`

const CountdownItems = styled.div`
  display: flex;
  gap: ${rem(6)};
`

const CountdownItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${rem(2)};
`

const CountdownValue = styled.span`
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: 1;
`

const CountdownLabel = styled.span`
  font-size: ${rem(12)};
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
`

function TourCampaignCountdown() {
  const [countdownTimeParts, setCountdownTimeParts] = useState<CountdownTimeParts | null>(() =>
    getCountdownTimeParts(TOUR_CAMPAIGN_CONFIG.endDate),
  )

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCountdownTimeParts(getCountdownTimeParts(TOUR_CAMPAIGN_CONFIG.endDate))
    }, 1000 * 60)

    return () => window.clearInterval(intervalId)
  }, [])

  if (!countdownTimeParts) {
    return null
  }

  return <CountdownWrapper $isUrgent={countdownTimeParts.isUrgent}>
    <BodyText variant="medium" weight="bold" colour="inherit">
      <FormattedMessage
        id="UWTYp2I89VwllLj5i1Sf"
        defaultMessage="Ends in"
        description="Prefix shown before the Asia tour campaign countdown on the tours homepage carousel"
      />
    </BodyText>
    <CountdownItems>
      <CountdownItem>
        <CountdownValue>{formatCountdownValue(countdownTimeParts.days)}</CountdownValue>
        <CountdownLabel>
          <FormattedMessage
            id="5NtGRpmu72t4HqMknI3t"
            defaultMessage="D"
            description="Abbreviated days label for the Asia tour campaign countdown on the tours homepage carousel"
          />
        </CountdownLabel>
      </CountdownItem>
      <CountdownItem>
        <CountdownValue>{formatCountdownValue(countdownTimeParts.hours)}</CountdownValue>
        <CountdownLabel>
          <FormattedMessage
            id="tFayaPG4IsXlEX_PddXo"
            defaultMessage="H"
            description="Abbreviated hours label for the Asia tour campaign countdown on the tours homepage carousel"
          />
        </CountdownLabel>
      </CountdownItem>
      <CountdownItem>
        <CountdownValue>{formatCountdownValue(countdownTimeParts.minutes)}</CountdownValue>
        <CountdownLabel>
          <FormattedMessage
            id="WlcHMACw4Cy7jtmpBF9Q"
            defaultMessage="M"
            description="Abbreviated minutes label for the Asia tour campaign countdown on the tours homepage carousel"
          />
        </CountdownLabel>
      </CountdownItem>
    </CountdownItems>
  </CountdownWrapper>
}

function TourHeroOfferCarousel(props: Props) {
  const intl = useIntl()
  const viewAllLabel = intl.formatMessage({
    id: 'qywu0sy_1NVQHCWxPYIf',
    defaultMessage: 'View all',
    description: 'Button text to view all available items in the current category or listing section',
  })
  const {
    filters = EmptyObject,
    random,
    onOffersShow,
  } = props

  const currentSiteTakeover = useCurrentSiteTakeover({ heroVertical: 'tours' })
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>()
  const isTourCampaignEnabled = isTourCampaignActive(region, config.BRAND)

  const carouselFilters = useMemo(() => {
    if (isTourCampaignEnabled) {
      return {
        ...filters,
        campaigns: [TOUR_CAMPAIGN_SEARCH_FILTER_TAG],
      }
    }

    if (currentSiteTakeover?.id === 'tourbestasia') {
      return {
        ...filters,
        destinations: selectedFilter ? [selectedFilter] : TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
      }
    }

    return filters
  }, [filters, currentSiteTakeover, isTourCampaignEnabled, selectedFilter])

  const asiaTakeoverPlaceIds = useMemo(
    () => [Places.Asia.id, Places.Vietnam.id, Places.Japan.id, Places.SriLanka.id],
    [],
  )
  const localizedAsiaTakeoverPlaces = useLocalizedPlaces(asiaTakeoverPlaceIds)

  const tourSearchURls = useMemo(() => {
    let urlSearchParams
    if (isTourCampaignEnabled) {
      urlSearchParams = new URLSearchParams({
        destinationId: ANYWHERE_PLACE_ID,
        destinationName: ANYWHERE_NAME,
        campaigns: TOUR_CAMPAIGN_SEARCH_FILTER_TAG,
      })
    } else if (currentSiteTakeover?.id === 'tourbestasia') {
      const { destinations, operators } = carouselFilters
      const asiaDestinationName = localizedAsiaTakeoverPlaces[Places.Asia.id]?.place?.name
      if (!asiaDestinationName) {
        return getLuxExclusiveToursSearchUrl()
      }
      urlSearchParams = new URLSearchParams({
        destinationId: Places.Asia.id,
        destinationName: asiaDestinationName,
        destinations,
        operators,
      })
    } else {
      return getLuxExclusiveToursSearchUrl()
    }
    return `/search/tours?${urlSearchParams.toString()}`
  }, [carouselFilters, currentSiteTakeover, isTourCampaignEnabled, localizedAsiaTakeoverPlaces])

  const filterOptions = useMemo<Array<{ label: string, value: string }>>(() => {
    if (!isTourCampaignEnabled && currentSiteTakeover?.id === 'tourbestasia') {
      return [
        { placeId: Places.Vietnam.id, value: Places.Vietnam.id },
        { placeId: Places.Japan.id, value: Places.Japan.id },
        { placeId: Places.SriLanka.id, value: Places.SriLanka.id },
      ].flatMap(({ placeId, value }) => {
        const label = localizedAsiaTakeoverPlaces[placeId]?.place?.name
        return label ? [{ label, value }] : []
      })
    }
    return []
  }, [currentSiteTakeover, isTourCampaignEnabled, localizedAsiaTakeoverPlaces])

  const toggleFilter = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const filter = e.currentTarget.dataset.value!
    const nextFilter = selectedFilter === filter ? undefined : filter
    setSelectedFilter(nextFilter)
  }, [selectedFilter])

  const offerList = useOfferList(carouselFilters, { randomize: random })
  const tracking = useOfferListTracking(getListName('OfferList', 'TOURS', 'Hero Carousel'), { key: getOfferListKey(filters) })

  const hasInitOffersLoaded = useRef(false)
  useEffect(() => {
    if (!hasInitOffersLoaded.current && !offerList.fetching && !!offerList.offerIds.length) {
      hasInitOffersLoaded.current = true
      onOffersShow?.(take(offerList.offerIds, 3))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerList])

  const paletteType = isTourCampaignEnabled ? 'inverse' : currentSiteTakeover?.palette ?? 'inverse'
  const headerPaletteType = isTourCampaignEnabled ? 'inverse' : currentSiteTakeover?.heroSection?.palette ?? 'inverse'
  // Campaign / takeover own the surface (background image + custom palette); skip default rebrand teal.
  const hasCustomHeroSurface = hasCustomHeroOfferCarouselSurface({
    siteTakeoverTheme: currentSiteTakeover,
    heroCarouselConfig: isTourCampaignEnabled ? { background: true } : currentSiteTakeover?.heroSection,
  })
  const {
    isRebrandExclusiveSurface,
    containerPalette,
    titleSheetPalette,
    carouselSheetPalette,
    tileStyle,
  } = getExclusiveOfferCarouselSurface({
    isRebrand: useRebrandVersion() === 'rebrand',
    hasCustomHeroSurface,
    palette: paletteType,
  })
  const headerSheetPalette = isRebrandExclusiveSurface ? titleSheetPalette : headerPaletteType

  return (
    <ExclusiveOfferContainer
      paletteType={containerPalette}
      className={clsx({ 'rebrand-highlight': isRebrandExclusiveSurface })}
    >
      <AgentHubEarnYourOwnTourBanner />
      {isTourCampaignEnabled && <SiteTakeoverBackground>
        <TourAsiaCampaignTakeoverBackground background="hero" />
      </SiteTakeoverBackground>}
      {!isTourCampaignEnabled && currentSiteTakeover && <SiteTakeoverBackground>
        <SiteTakeoverBackgroundImage background="hero" vertical="tours" />
      </SiteTakeoverBackground>}
      <Content direction="vertical" gap={24} tabletGap={32}>
        <LayoutContainer>
          <TopExclusiveHeaderGrid className={isTourCampaignEnabled ? 'campaign' : undefined} paletteType={headerSheetPalette}>
            <Group className="title-area" direction="horizontal" gap={12}>
              {isTourCampaignEnabled && <TourAsiaCampaignProfileImage />}
              {!isTourCampaignEnabled && currentSiteTakeover?.heroSection?.logo}
              {!isTourCampaignEnabled && <Group direction="vertical" verticalAlign="center">
                {!currentSiteTakeover?.heroSection?.title && <Heading className="title-area" variant="heading2" align="start">
                  <FormattedMessage
                    id="l3wbcUEHDJMUgG_szQyX"
                    defaultMessage="Today’s top <i>exclusive</i> offers"
                    description="Top picks for me carousel title with italic 'exclusive'"
                    values={{
                      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                    }}
                  />
                </Heading>}
                {currentSiteTakeover?.heroSection?.title}
                {currentSiteTakeover?.heroSection?.subtitle}
              </Group>}
              {isTourCampaignEnabled && <CampaignHeaderContent>
                <CampaignTitleRow>
                  <Heading variant="heading2">
                    <FormattedMessage
                      id="PAXPSbxdLX3t9Oj1WoF5"
                      defaultMessage="Asia Tours on Sale"
                      description="Heading title for tour best of Asia promotional campaign"
                    />
                  </Heading>
                  <CampaignActions direction="horizontal" gap={16} verticalAlign="center">
                    <TourCampaignCountdown />
                    <CarouselViewAllLink to={tourSearchURls}>{viewAllLabel}</CarouselViewAllLink>
                  </CampaignActions>
                </CampaignTitleRow>
                <TourAsiaCampaignHeroCarouselSubtitle />
              </CampaignHeaderContent>}
            </Group>
            {!isTourCampaignEnabled && !!filterOptions.length && <PaletteSheet className="filters-area" paletteType="default">
              <Group direction="horizontal" gap={8}>
                {filterOptions.map(option =>
                  <FilterChip
                    key={option.value}
                    size="medium"
                    selected={option.value === selectedFilter}
                    data-value={option.value}
                    onClick={toggleFilter}
                  >
                    {option.label}
                  </FilterChip>,
                )}
              </Group>
            </PaletteSheet>}
            {!isTourCampaignEnabled && <CarouselViewAllLink className="link-area" to={tourSearchURls}>{viewAllLabel}</CarouselViewAllLink>}
          </TopExclusiveHeaderGrid>
        </LayoutContainer>
        <Sheet paletteType={carouselSheetPalette} background="none">
          <OfferListEventsProvider tracking={tracking}>
            <OfferListCarousel
              tileStyle={tileStyle}
              tileSize="medium"
              overrideOfferList={offerList}
              limit={15}
            >
              <HeroOfferCarouselLookingForSomethingElse
                link={tourSearchURls}
                offerType="tours"
              />
            </OfferListCarousel>
          </OfferListEventsProvider>
        </Sheet>
      </Content>
    </ExclusiveOfferContainer>
  )
}

export default TourHeroOfferCarousel
